import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportAccountingShipments } from '../../hooks/useImportAccountingShipments';

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xltx', '.xltm'];
const MAX_BYTES = 10 * 1024 * 1024;

export interface ImportShipmentsModalRef {
  open: (onSuccess?: () => void) => void;
  close: () => void;
}

function getExtension(name: string): string {
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index).toLowerCase() : '';
}

export const ImportShipmentsModal = forwardRef<ImportShipmentsModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesOrdersModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onSuccessRef = useRef<(() => void) | undefined>(undefined);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { importAccountingShipments, isImporting } = useImportAccountingShipments();

  const resetState = () => {
    setSelectedFile(null);
    setDragActive(false);
    setError(null);
    onSuccessRef.current = undefined;
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (onSuccess?: () => void) => {
        resetState();
        onSuccessRef.current = onSuccess;
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }),
    [],
  );

  const validateFile = (file?: File): boolean => {
    if (!file) {
      return false;
    }

    const ext = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(t('import.error.invalid_type', 'Formato no permitido. Usa .xlsx, .xls, .xlsm, .xltx o .xltm.'));
      return false;
    }

    if (file.size > MAX_BYTES) {
      setError(t('import.error.too_large', 'El archivo supera el límite de 10 MB.'));
      return false;
    }

    setSelectedFile(file);
    setError(null);
    return true;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isImporting) {
      return;
    }
    validateFile(event.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (isImporting) {
      return;
    }
    validateFile(event.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError(t('import.modal.no_file', 'Selecciona un archivo Excel.'));
      return;
    }

    try {
      await importAccountingShipments(selectedFile, onSuccessRef.current);
      modalRef.current?.closeModal();
    } catch {
      /* toast en hook */
    }
  };

  const dropzoneClasses = [
    'flex flex-col items-center justify-center gap-2 px-6 py-6 rounded-lg border-2 border-dashed transition-colors cursor-pointer',
    dragActive ? 'border-primary bg-primary/5' : 'border-card-border bg-card-bg hover:border-primary/50',
    isImporting ? 'opacity-60 pointer-events-none' : '',
  ].join(' ');

  return (
    <ModalCenter
      ref={modalRef}
      title={t('import.modal.title', 'Importar accounting shipments')}
      width="lg"
      type="default"
    >
      <div className="space-y-6">
        <p className="text-sm text-complementary-text">
          {t(
            'import.modal.description',
            'Sube un Excel con precios por pedido. Se crearán accounting_shipments (SHIPPING y, si aplica, RETURN) y se descargará un Excel con el resultado fila a fila.',
          )}
        </p>

        <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-2">
          <div className="flex items-start gap-2 text-sm text-title">
            <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4 text-icons mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>{t('import.modal.columns_hint', 'Columnas del Excel de entrada:')}</p>
              <ul className="list-disc list-inside text-complementary-text space-y-0.5">
                <li>{t('import.modal.col_order_id', 'Id Pedido (obligatorio)')}</li>
                <li>{t('import.modal.col_status', 'Estatus Interno o Estatus logístico (obligatorio)')}</li>
                <li>
                  {t('import.modal.col_prices', 'Precio envío ida, COD, picking ida/vuelta (opcionales, base sin IVA)')}
                </li>
                <li>{t('import.modal.col_weight', 'Peso Beeping (opcional)')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={dropzoneClasses}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isImporting) setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onClick={() => !isImporting && inputRef.current?.click()}
          data-testid="import-shipments-dropzone"
        >
          {selectedFile ? (
            <div className="text-center space-y-1">
              <FontAwesomeIcon icon={faArrowUpFromBracket} className="!h-10 !w-10 text-primary" />
              <p className="text-sm text-title font-medium pt-2">
                {t('import.modal.selected_file', 'Archivo seleccionado')}:{' '}
                <span className="text-complementary-text">{selectedFile.name}</span>
              </p>
            </div>
          ) : (
            <div className="text-center space-y-1">
              <FontAwesomeIcon icon={faArrowUpFromBracket} className="h-9 w-9 text-icons" />
              <p className="text-sm text-complementary-text pt-2">
                {t('import.modal.dropzone', 'Arrastra un Excel aquí o haz clic para seleccionarlo')}
              </p>
              <p className="text-xs text-complementary-text">
                {t('import.modal.format_hint', '.xlsx, .xls, .xlsm, .xltx, .xltm — máx. 10 MB')}
              </p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.xlsm,.xltx,.xltm"
            className="hidden"
            onChange={handleInputChange}
            data-testid="import-shipments-input"
          />
        </div>

        {error && (
          <p className="text-sm text-error" data-testid="import-shipments-error">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isImporting}>
            {t('buttons.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={isImporting || !selectedFile}
            data-testid="import-shipments-submit"
          >
            {isImporting ? t('import.modal.importing', 'Importando...') : t('import.modal.import', 'Importar')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ImportShipmentsModal.displayName = 'ImportShipmentsModal';
